import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@1trading/react';
import { CircleX, Filter } from 'lucide-react';
import { useId, useState } from 'react';
import { TRADE_SIDES, type TradeSide, type TradesFilters } from '@/services/api/trades';
import { localEndOfDayIso, localStartOfDayIso, toDateInputValue } from '@/utils/date';
import { parseFloatOrUndefined } from '@/utils/number';

export type TradeFilterField = keyof TradesFilters;

type TradeFilterType = 'multi-choice' | 'select' | 'range' | 'text' | 'multi-text' | 'date';

interface FilterConfig {
  field: TradeFilterField;
  label: string;
  type: TradeFilterType;
}

/**
 * Shape of the ops Trading page's filter row. The user-console Trades tab
 * folds in here as a restricted config instead of a parallel component:
 * - Exchange sub-tab: showAccountHolder=false (every query is locked to the pid),
 *   showPrice=true, instrumentType='multi-choice';
 * - Instant sub-tab: showAccountHolder=false, showPrice=false (legacy
 *   price-filter-type: null), instrumentType='multi-text' — instant-trade markets
 *   aren't guaranteed to be in the FAST instruments list, so the market filter is
 *   free-text there instead of a checkbox list.
 */
export interface TradeFiltersConfig {
  showAccountHolder?: boolean;
  showPrice?: boolean;
  instrumentType?: 'multi-choice' | 'multi-text';
}

const DEFAULT_CONFIG: Required<TradeFiltersConfig> = { showAccountHolder: true, showPrice: true, instrumentType: 'multi-choice' };

/** Chip order + labels = the filterable legacy columns, in column order. */
const buildFilterConfigs = (config: TradeFiltersConfig): FilterConfig[] => {
  const { showAccountHolder, showPrice, instrumentType } = { ...DEFAULT_CONFIG, ...config };
  return [
    { field: 'instrument', label: 'Market', type: instrumentType },
    { field: 'side', label: 'Side', type: 'select' },
    ...(showPrice ? [{ field: 'price', label: 'Price', type: 'range' } as const] : []),
    ...(showAccountHolder ? [{ field: 'accountHolderId', label: 'Account Holder ID', type: 'text' } as const] : []),
    { field: 'tradeId', label: 'Trade ID', type: 'multi-text' },
    { field: 'time', label: 'Date / Time', type: 'date' },
  ];
};

const removeField = (filters: TradesFilters, field: TradeFilterField): TradesFilters => {
  const { [field]: _removed, ...rest } = filters;
  return rest;
};

interface TradeFilterDialogProps {
  config: FilterConfig;
  filters: TradesFilters;
  marketIds: string[];
  onApply: (next: TradesFilters) => void;
  onClose: () => void;
}

const TradeFilterDialog = ({ config, filters, marketIds, onApply, onClose }: TradeFilterDialogProps) => {
  const inputId = useId();
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(filters.instrument ?? []);
  const [side, setSide] = useState<TradeSide | ''>(filters.side ?? '');
  const [text, setText] = useState(
    config.field === 'accountHolderId' ? (filters.accountHolderId ?? '') : config.field === 'instrument' ? (filters.instrument?.[0] ?? '') : (filters.tradeId?.[0] ?? ''),
  );
  const [rangeFrom, setRangeFrom] = useState(filters.price?.from !== undefined ? String(filters.price.from) : '');
  const [rangeTo, setRangeTo] = useState(filters.price?.to !== undefined ? String(filters.price.to) : '');
  const [dateFrom, setDateFrom] = useState(filters.time?.from ? toDateInputValue(new Date(filters.time.from)) : '');
  const [dateTo, setDateTo] = useState(filters.time?.to ? toDateInputValue(new Date(filters.time.to)) : '');

  // "Clear" empties the dialog fields only — the filter itself changes on Apply
  const handleClear = () => {
    setSelectedMarkets([]);
    setSide('');
    setText('');
    setRangeFrom('');
    setRangeTo('');
    setDateFrom('');
    setDateTo('');
  };

  // Empty values delete the filter key entirely
  const buildNextFilters = (): TradesFilters => {
    const rest = removeField(filters, config.field);
    switch (config.type) {
      case 'multi-choice':
        return selectedMarkets.length > 0 ? { ...rest, instrument: selectedMarkets } : rest;
      case 'select':
        return side ? { ...rest, side } : rest;
      case 'range': {
        const from = parseFloatOrUndefined(rangeFrom);
        const to = parseFloatOrUndefined(rangeTo);
        if (from === undefined && to === undefined) return rest;
        return { ...rest, price: { ...(from === undefined ? {} : { from }), ...(to === undefined ? {} : { to }) } };
      }
      case 'text': {
        const trimmed = text.trim();
        return trimmed ? { ...rest, accountHolderId: trimmed } : rest;
      }
      case 'multi-text': {
        // One text input commits a single-element array (legacy multi-text) —
        // used for tradeId everywhere and for the free-text instrument filter on the
        // user-console Instant sub-tab
        const trimmed = text.trim();
        if (!trimmed) return rest;
        return config.field === 'instrument' ? { ...rest, instrument: [trimmed] } : { ...rest, tradeId: [trimmed] };
      }
      case 'date': {
        if (!dateFrom && !dateTo) return rest;
        return { ...rest, time: { ...(dateFrom ? { from: localStartOfDayIso(dateFrom) } : {}), ...(dateTo ? { to: localEndOfDayIso(dateTo) } : {}) } };
      }
    }
  };

  const toggleMarket = (marketId: string, isChecked: boolean) =>
    setSelectedMarkets((previous) => (isChecked ? [...previous, marketId] : previous.filter((value) => value !== marketId)));

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="size-4" />
            Edit {config.label} Filter
          </DialogTitle>
        </DialogHeader>
        {config.type === 'multi-choice' ? (
          // Options keep the FAST endpoint order — deliberately unsorted
          <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
            {marketIds.map((marketId) => (
              <label className="flex cursor-pointer items-center gap-2 text-sm" htmlFor={`${inputId}-${marketId}`} key={marketId}>
                <Checkbox checked={selectedMarkets.includes(marketId)} id={`${inputId}-${marketId}`} onCheckedChange={(checked) => toggleMarket(marketId, checked === true)} />
                {marketId}
              </label>
            ))}
          </div>
        ) : null}
        {config.type === 'select' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={inputId}>{config.label}</Label>
            <Select onValueChange={(value) => setSide(value as TradeSide)} value={side}>
              <SelectTrigger id={inputId}>
                <SelectValue placeholder="Select side" />
              </SelectTrigger>
              <SelectContent>
                {TRADE_SIDES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {config.type === 'range' ? (
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`${inputId}-from`}>From</Label>
              <Input id={`${inputId}-from`} onChange={(event) => setRangeFrom(event.target.value)} type="number" value={rangeFrom} />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`${inputId}-to`}>To</Label>
              <Input id={`${inputId}-to`} onChange={(event) => setRangeTo(event.target.value)} type="number" value={rangeTo} />
            </div>
          </div>
        ) : null}
        {config.type === 'text' || config.type === 'multi-text' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={inputId}>{config.label}</Label>
            <Input id={inputId} onChange={(event) => setText(event.target.value)} value={text} />
          </div>
        ) : null}
        {config.type === 'date' ? (
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`${inputId}-from`}>From</Label>
              <Input id={`${inputId}-from`} onChange={(event) => setDateFrom(event.target.value)} type="date" value={dateFrom} />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`${inputId}-to`}>To</Label>
              <Input id={`${inputId}-to`} onChange={(event) => setDateTo(event.target.value)} type="date" value={dateTo} />
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button onClick={handleClear} type="button" variant="outline">
            Clear
          </Button>
          <Button onClick={() => onApply(buildNextFilters())} type="button" variant="primary">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
TradeFilterDialog.displayName = 'TradeFilterDialog';

interface TradeFiltersRowProps {
  /** Restricts the chip set / field types; defaults to the full ops filter row. */
  config?: TradeFiltersConfig;
  filters: TradesFilters;
  /** Market filter options; the Market chip stays hidden until these load */
  marketIds: string[];
  onChange: (filters: TradesFilters) => void;
}

export const TradeFiltersRow = ({ config, filters, marketIds, onChange }: TradeFiltersRowProps) => {
  const [editing, setEditing] = useState<FilterConfig | null>(null);
  const hasActiveFilters = Object.keys(filters).length > 0;
  const filterConfigs = buildFilterConfigs(config ?? {});

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium text-muted-foreground">
        <Filter className="size-3.5" />
        Filters
      </span>
      {filterConfigs.map((filterConfig) => {
        // Legacy `filterable: marketOptions.length > 0` — no dropdown chip until options
        // exist (the free-text instrument chip has no such dependency)
        if (filterConfig.type === 'multi-choice' && marketIds.length === 0) return null;
        const isActive = filters[filterConfig.field] !== undefined;
        // Array-valued filters show a count badge; the rest signal by color only
        const count = filterConfig.field === 'instrument' ? (filters.instrument?.length ?? 0) : filterConfig.field === 'tradeId' ? (filters.tradeId?.length ?? 0) : 0;
        return (
          <Button className="rounded-full" key={filterConfig.field} onClick={() => setEditing(filterConfig)} size="sm" type="button" variant={isActive ? 'default' : 'outline'}>
            {filterConfig.label}
            {count > 0 ? <span className="rounded-full bg-background/25 px-1.5 text-xs tabular-nums">{count}</span> : null}
          </Button>
        );
      })}
      {hasActiveFilters ? (
        <Button className="rounded-full gap-1.5" onClick={() => onChange({})} size="sm" type="button" variant="secondary">
          <CircleX className="size-3.5" />
          Clear All
        </Button>
      ) : null}
      {editing ? (
        <TradeFilterDialog
          config={editing}
          filters={filters}
          marketIds={marketIds}
          onApply={(next) => {
            onChange(next);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
};
TradeFiltersRow.displayName = 'TradeFiltersRow';
